import { useState, useEffect, useCallback, useRef } from "react";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { StripePaymentElementOptions } from "@stripe/stripe-js";
import {
  fetchStripeCustomer,
  fetchStripePaymentMethods,
  updateStripeCustomerDefaultPaymentMethod,
} from "@campgladiator/cg-common.api.stripe";
import { DefaultPaymentMethod } from "@campgladiator/cg-common.api.stripe";
import { PaymentMethodsProps } from "../components/PaymentMethods";

export type UsePaymentManagementProps = {
  stripeCustomerId: string;
  onError: (error: any) => void;
  onSelectPaymentMethod: (paymentMethodId: string) => void;
};

const usePaymentManagement = ({
  stripeCustomerId,
  onError,
  onSelectPaymentMethod,
}: UsePaymentManagementProps) => {
  const [defaultPaymentDetails, setDefaultPaymentDetails] = useState<
    DefaultPaymentMethod[]
  >([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    DefaultPaymentMethod | undefined
  >(undefined);
  const [isAddingNewCard, setIsAddingNewCard] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isFirstLoad = useRef(false);

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: "tabs",
  };
  const stripe = useStripe();
  const elements = useElements();

  const showPaymentMethods =
    defaultPaymentDetails?.length > 0 && !isAddingNewCard;

  const onSubmitNewCard = async (e: any) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    try {
      setIsLoading(true);
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          payment_method_data: {
            billing_details: {},
          },
        },
      });
      if (error) throw error;
      const stripePaymentMethodId = setupIntent?.payment_method as string;
      onSelectPaymentMethod(stripePaymentMethodId);
      onSelectPaymentMethodOption({
        id: setupIntent?.payment_method as string,
      } as DefaultPaymentMethod);

      await updateStripeCustomerDefaultPaymentMethod(
        stripeCustomerId,
        stripePaymentMethodId
      );

      setIsAddingNewCard(false);
    } catch (err: any) {
      if (err.type === "card_error" || err.type === "validation_error") {
        onError(err.message as string);
      } else {
        onError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSelectPaymentMethodOption = (paymentMethod: DefaultPaymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    onSelectPaymentMethod(paymentMethod.id);
  };

  const onAddNewCardClick = () => {
    setIsAddingNewCard(true);
  };

  const onCancelAddingNewCard = () => {
    setIsAddingNewCard(false);
  };

  const paymentMethodsProps: PaymentMethodsProps = {
    defaultPaymentDetails,
    onSelectPaymentMethodOption,
    selectedPaymentMethod,
    onAddNewCardClick,
  };

  const loadPaymentMethods = useCallback(async () => {
    if (stripeCustomerId) {
      fetchStripeCustomer(stripeCustomerId).then((customerResponse) => {
        const defaultPaymentMethodId =
          customerResponse.invoice_settings.default_payment_method;
        fetchStripePaymentMethods(stripeCustomerId)
          .then((stripe) => {
            const paymentMethodsFilledWithDefault: DefaultPaymentMethod[] =
              stripe.data.map((paymentMethod) => {
                const pm = {
                  ...paymentMethod,
                  default: paymentMethod.id === defaultPaymentMethodId,
                };
                if (pm.default && !isFirstLoad.current) {
                  onSelectPaymentMethodOption(pm);
                  isFirstLoad.current = true;
                }
                return pm;
              });
            setDefaultPaymentDetails(paymentMethodsFilledWithDefault);
          })
          .catch(async (err) => {
            onError(err);
          });
      });
    }
  }, [stripeCustomerId, onError, isAddingNewCard]);

  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  return {
    paymentElementOptions,
    showPaymentMethods,
    paymentMethodsProps,
    onCancelAddingNewCard,
    onSubmitNewCard,
    isLoading,
  };
};

export default usePaymentManagement;
